import { handleApiError } from "../utils/errorHandler";

const baseUrl = "/transactions";

const getCurrencyPairAdjustment = (fromCurrency, toCurrency) => {
  const pairAdjustments = {
    'XOF_EUR': 0.005,
    'XOF_USD': 0.004,
    'EUR_XOF': 0.002,
    'USD_XOF': 0.003
  };
  const pairKey = `${fromCurrency}_${toCurrency}`;
  return pairAdjustments[pairKey] || 0.002;
};

const calculateFees = (data) => {
    const { fromAmount, fromCurrency, toCurrency, affiliate } = data;
    const amount = parseFloat(fromAmount);
    
    const baseFeeRate = 0.01;
    const networkFeeRate = 0.002;
    const liquidityFeeRate = 0.003;
    const partnerFeeRate = 0.001;
    
    const currencyPairAdjustment = getCurrencyPairAdjustment(fromCurrency, toCurrency);
    
    const baseFee = amount * baseFeeRate;
    const networkFee = amount * networkFeeRate;
    const liquidityFee = amount * liquidityFeeRate;
    const partnerFee = amount * partnerFeeRate;
    const currencyPairFee = amount * currencyPairAdjustment;
    
    const affiliateDiscount = affiliate ? amount * 0.005 : 0;
    
    const totalFees = baseFee + networkFee + liquidityFee + partnerFee + currencyPairFee - affiliateDiscount;
    
    const roundToFour = (num) => Math.round(num * 10000) / 10000;
    
    return {
      baseFee: roundToFour(baseFee),
      networkFee: roundToFour(networkFee),
      liquidityFee: roundToFour(liquidityFee),
      partnerFee: roundToFour(partnerFee),
      currencyPairFee: roundToFour(currencyPairFee),
      affiliateDiscount: roundToFour(affiliateDiscount),
      totalFees: roundToFour(totalFees),
      feesPercentage: roundToFour(totalFees / amount * 100),
      fromAmount: amount,
      netAmount: roundToFour(amount - totalFees),
      currency: fromCurrency
    };
};

export const createTransactionService = (api) => ({
  async getTransactions(params = { limit: 10, page: 1, status: null }) {
    try {
      const response = await api.get(baseUrl, { params });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des transactions:", error);
      return handleApiError(error, "transaction.list", () => ({
        transactions: [],
        pagination: { total: 0, page: 1, limit: 10, pages: 0 },
      }));
    }
  },

  async getTransactionById(id) {
    try {
      const response = await api.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la transaction ${id}:`, error);
      return handleApiError(error, "transaction.details", () => ({
        transaction: null,
        message: "Transaction introuvable",
      }));
    }
  },

  async confirmTransaction(id) {
    let attempts = 0;
    const maxAttempts = 3;
    while (attempts < maxAttempts) {
      try {
        const response = await api.post(`${baseUrl}/${id}/confirm`);
        return response.data;
      } catch (error) {
        attempts++;
        console.error(`Tentative ${attempts}/${maxAttempts} - Erreur lors de la confirmation:`, error);
        if (error.response?.status === 429 && attempts < maxAttempts) {
          const delay = 2000 * Math.pow(2, attempts - 1);
          console.log(`Nouvelle tentative dans ${delay / 1000}s...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          throw error;
        }
      }
    }
  },

  async cancelTransaction(id) {
    try {
      const response = await api.post(`${baseUrl}/${id}/cancel`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de l'annulation de la transaction ${id}:`, error);
      throw error;
    }
  },

  async createTransaction(transactionData) {
    try {
      const response = await api.post(baseUrl, transactionData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de la transaction:", error);
      throw error;
    }
  },

  async createExchangeTransaction(data) {
    try {
      const feesBreakdown = calculateFees(data);
      const payload = { ...data, feesBreakdown };
      const response = await api.post(`${baseUrl}/exchange`, payload);
      return { ...response.data, feesBreakdown };
    } catch (error) {
      console.error("Error creating exchange transaction:", error);
      throw error;
    }
  },

  async createManualConfirmationTransaction(transactionData) {
    try {
      const response = await api.post(`${baseUrl}/manual-confirmation`, transactionData);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de la transaction pour confirmation manuelle:", error);
      throw error; 
    }
  },

  async updateTransactionStatus(trackingNumber, status) {
    try {
      const response = await api.patch(`${baseUrl}/status`, { trackingNumber, status });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      throw error;
    }
  },

  async getTransactionStatistics() {
    try {
      const response = await api.get(`${baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques de transaction:", error);
      return { totalVolume: 0, totalTransactions: 0, pendingTransactions: 0 };
    }
  },
});